/**
 * Group of Related Content Components.
 */
import { Component, registerComponent } from 'js/component.js';
import { DateTime } from 'luxon';

const componentName = 'related-content-group';
const relatedContentClassName = 'related-content';
const contentTileClassName = 'content-tile';

class RelatedContentGroup extends Component {

    /**
     * Component Setup
     */
    elements = {
        body: document.body,
        relatedContentContainerElements: this.element.querySelectorAll(`.${relatedContentClassName}__container:not(ra-header .${relatedContentClassName}__container)`),
        nonGridContentTileContainerElements:
            this.element.querySelectorAll(`.${contentTileClassName}__container[data-type="dynamic"]:not(.${contentTileClassName}__related__container, ra-header .${contentTileClassName}__related__container)`),
        staticContentWebinarContainerElements:
            this.element.querySelectorAll(`.${contentTileClassName}__container[data-webinar-type][data-type="static"]`),
    };
    baseUrl = this.element.getAttribute('data-resource-path');
    currentPagePath = this.element.getAttribute('data-current-page-path')?.replace('/content/preview/', '/content/');
    previewToken = this.elements.relatedContentContainerElements[0]?.getAttribute('data-preview-token') ? this.elements.relatedContentContainerElements[0]?.getAttribute('data-preview-token') : this.elements.nonGridContentTileContainerElements[0]?.getAttribute('data-preview-token');
    queryString = `?${this.currentPagePath ? `currentPath=${this.currentPagePath}` : ''}${(this.currentPagePath && (this.previewToken && this.previewToken !== '')) ? '&' : ''}${(this.previewToken && this.previewToken !== '') ? `${this.previewToken}` : ''}`;
    finalUrl = `${this.baseUrl}.relatedContent.html${(this.queryString !== '?') ? this.queryString : ''}`;
    relatedContentMapFromDom = {};
    nonGridContentTilesMapFromDom = {};

    /**
     * Initialize Component
     */
    init() {
        if ((this.elements.relatedContentContainerElements != null && this.elements.relatedContentContainerElements.length > 0)
            || ((this.elements.nonGridContentTileContainerElements != null && this.elements.nonGridContentTileContainerElements.length > 0))) {

            // First, show all the Loading Icons for the NonGrid Tiles & Grid-type RelatedContent components.
            this.showLoaderForAllTiles(this.elements.nonGridContentTileContainerElements);
            this.showLoaderForAllGrids(this.elements.relatedContentContainerElements);

            // Find all the nonGrid content-tile container elements in the DOM & convert them into a map.
            this.nonGridContentTilesMapFromDom = this.getContentTileMap(this.elements.nonGridContentTileContainerElements);

            // Find all the related-content container elements in the DOM & convert them into a map.
            this.relatedContentMapFromDom = this.getRelatedContentMap(this.elements.relatedContentContainerElements);

            this.loadDynamicContent();
        }

        if (this.elements.staticContentWebinarContainerElements != null && this.elements.staticContentWebinarContainerElements.length > 0) {
            this.updateStaticWebinarTiles();
        }
    }

    /**
     * Load Content for all the Related Content Components.
     */
    loadDynamicContent = async () => {
        try {
            const response = await fetch(this.finalUrl);
            if (!response.ok) {
                throw new Error('Response failed');
            }
            const responseData = await response.text();
            if (response.status === 200 && responseData) {
                // Convert the HTML string to a a HTML element.
                const newItem = document.createElement('div');
                newItem.innerHTML = responseData.trim();

                // Find all the nonGrid content-tile container elements in the response & convert them into a map.
                const nonGridContentTileContainerElementsFromResponse = newItem.querySelectorAll(`.${contentTileClassName}__container:not(.${contentTileClassName}__related__container)`);
                const nonGridContentTilesMapFromResponse = this.getContentTileMap(nonGridContentTileContainerElementsFromResponse);

                // Find all the related-content container elements in the response & convert them into a map.
                const relatedContentContainerElementsFromResponse = newItem.querySelectorAll(`.${relatedContentClassName}__container`);
                const relatedContentMapFromResponse = this.getRelatedContentMap(relatedContentContainerElementsFromResponse);

                // Hide all the Loading Icons for the NonGrid Tiles & Grid-type RelatedContent components.
                this.hideLoaderForAllTiles(this.elements.nonGridContentTileContainerElements);
                this.hideLoaderForAllGrids(this.elements.relatedContentContainerElements);

                // Iterate thru the available elements from DOM (for both Grid related-content & nonGrid content-tile elements)
                // Pass the key (cmp ID), the map created from response & the map created from DOM to the callback method.
                Object.keys(this.nonGridContentTilesMapFromDom).forEach((contentTileId) => {
                    this.loadSingleContentTile(contentTileId, nonGridContentTilesMapFromResponse, this.nonGridContentTilesMapFromDom);
                });
                Object.keys(this.relatedContentMapFromDom).forEach((relatedContentId) => {
                    this.loadSingleRelatedContentGrid(relatedContentId, relatedContentMapFromResponse, this.relatedContentMapFromDom);
                });

                window.piraContentTilesLoaded = true;
                window.dispatchEvent(new Event('content-tiles-loaded'));
                // Dispatch resize event for carousel compatibility
                window.dispatchEvent(new Event('init-carousel'));

            } else {
                // The response is null, so hide all the Loading Icons.
                this.hideLoaderForAllTiles(this.elements.nonGridContentTileContainerElements);
                this.hideLoaderForAllGrids(this.elements.relatedContentContainerElements);
            }
        } catch (e) {
            // There is an exception caught. So, hide all the Loading Icons (in case they they are displayed).
            this.hideLoaderForAllTiles(this.elements.nonGridContentTileContainerElements);
            this.hideLoaderForAllGrids(this.elements.relatedContentContainerElements);
        }
    };

    /**
     * Loads data for a single RelatedContent Grid (i.e. all the tiles in the Grid)
     * @param relatedContentId - the id for the related content component.
     * @param relatedContentMapFromResponse - the mapping from ids to the elements (for the elements in the dynamic response).
     * @param relatedContentMapFromDom - the mapping from ids to the elements (for the elements in the DOM)
     */
    loadSingleRelatedContentGrid = (relatedContentId, relatedContentMapFromResponse, relatedContentMapFromDom) => {
        const relatedContentContainerElementFromResponse = relatedContentMapFromResponse[relatedContentId];
        const relatedContentContainerElementFromDom = relatedContentMapFromDom[relatedContentId];
        if (relatedContentContainerElementFromResponse && relatedContentContainerElementFromDom) {
            // Find all the content-tile container elements (resp. to the related-content) in the response & convert them into a map.
            const contentTileContainerElementsFromResponse = relatedContentContainerElementFromResponse.querySelectorAll(`.${contentTileClassName}__container[data-type="dynamic"]`);
            const contentTileMapFromResponse = this.getContentTileMap(contentTileContainerElementsFromResponse);

            // Find all the content-tile container elements (resp. to the related-content) in the DOM & convert them into a map.
            const contentTileContainerElementsFromDom = relatedContentContainerElementFromDom.querySelectorAll(`.${contentTileClassName}__container[data-type="dynamic"]`);
            const contentTileMapFromDom = this.getContentTileMap(contentTileContainerElementsFromDom);

            // Iterate thru the available elements from DOM
            // Pass the key (cmp ID), the map created from response & the map created from DOM to the callback method.
            Object.keys(contentTileMapFromDom).forEach((contentTileId) => {
                this.loadSingleContentTile(contentTileId, contentTileMapFromResponse, contentTileMapFromDom);
            });
        }
    };

    /**
     * Loads data for a single ContentTile.
     * @param contentTileId - the id of the content tile.
     * @param contentTileMapFromResponse - the mapping from ids to the elements (for the elements in the dynamic response).
     * @param contentTileMapFromDom - the mapping from ids to the elements (for the elements in the DOM)
     */
    loadSingleContentTile = (contentTileId, contentTileMapFromResponse, contentTileMapFromDom) => {
        const contentTileContainerElementFromResponse = contentTileMapFromResponse[contentTileId];
        const contentTileContainerElementFromDom = contentTileMapFromDom[contentTileId];
        if (contentTileContainerElementFromResponse && contentTileContainerElementFromDom) {
            const parentElem = contentTileContainerElementFromDom.parentElement;
            parentElem.removeChild(contentTileContainerElementFromDom);
            parentElem.appendChild(contentTileContainerElementFromResponse);

            const contentTileWebinarType = contentTileContainerElementFromResponse.getAttribute('data-webinar-type');
            if (contentTileWebinarType) {
                this.updateWebinarTile(contentTileId);
            }
        }
    };

    /**
     * Util method to transform a set of HTML elements ("related-content__container" elements)
     * to a map - keys are Ids & values are the resp. HTML elements.
     * @param relatedContentContainerElements the set of HTML elements.
     * @returns {{a map}}
     */
    getRelatedContentMap = (relatedContentContainerElements) => {
        const relatedContentMap = {};
        for (let i = 0; i < relatedContentContainerElements.length; i += 1) {
            const relatedContentContainerElement = relatedContentContainerElements[i];
            relatedContentMap[relatedContentContainerElement.id] = relatedContentContainerElement;
        }
        return relatedContentMap;
    };

    /**
     * Util method to transform a set of HTML elements ("content-tile__container" elements)
     * to a map representing - Keys are Ids & values are the resp. HTML elements
     * @param contentTileContainerElements the set of HTML elements.
     * @returns {{a map}}
     */
    getContentTileMap = (contentTileContainerElements) => {
        const contentTilesMap = {};
        for (let i = 0; i < contentTileContainerElements.length; i += 1) {
            const contentTileContainerElem = contentTileContainerElements[i];
            contentTilesMap[contentTileContainerElem.id] = contentTileContainerElem;
        }
        return contentTilesMap;
    };

    /**
     * Shows Loading Icons for all Grids.
     * @param relatedContentContainerElements - the related content container elements
     */
    showLoaderForAllGrids = (relatedContentContainerElements) => {
        for (let i = 0; i < relatedContentContainerElements.length; i += 1) {
            const relatedContentContainerElement = relatedContentContainerElements[i];
            const loaderElem = relatedContentContainerElement.querySelector('.loader');
            loaderElem.classList.add('active');
            loaderElem.style.display = 'block';
        }
    };

    /**
     * Shows Loading Icons for all Tiles.
     * @param contentTileContainerElements - the content-tile container elements.
     */
    showLoaderForAllTiles = (contentTileContainerElements) => {
        for (let i = 0; i < contentTileContainerElements.length; i += 1) {
            const contentTileContainerElement = contentTileContainerElements[i];
            const loaderElem = contentTileContainerElement.querySelector('.loader');
            loaderElem.classList.add('active');
            loaderElem.style.display = 'block';
        }
    };

    /**
     * Hides Loading Icons for all Grids.
     * @param relatedContentContainerElements - the related content container elements
     */
    hideLoaderForAllGrids = (relatedContentContainerElements) => {
        for (let i = 0; i < relatedContentContainerElements.length; i += 1) {
            const relatedContentContainerElement = relatedContentContainerElements[i];
            const loaderElem = relatedContentContainerElement.querySelector('.loader');
            loaderElem.classList.remove('active');
            loaderElem.style.display = 'none';
        }
    };

    /**
     * Hides Loading Icons for all Tiles.
     * @param contentTileContainerElements - the content-tile container elements.
     */
    hideLoaderForAllTiles = (contentTileContainerElements) => {
        for (let i = 0; i < contentTileContainerElements.length; i += 1) {
            const contentTileContainerElement = contentTileContainerElements[i];
            const loaderElem = contentTileContainerElement.querySelector('.loader');
            loaderElem.classList.remove('active');
            loaderElem.style.display = 'none';
        }
    };

    updateWebinarTile = (tileId) => {
        const webinarTile = document.getElementById(tileId);
        const tileDate = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.startDate`);
        const tileTime = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.startTime`);
        const tileLength = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.length`);
        const tileLang = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.language`);
        const webinarType = webinarTile.getAttribute('data-webinar-type');
        const webinarOnDemand = webinarTile.getAttribute('data-webinar-on-demand');
        const webinarTimeZone = webinarOnDemand != null ? webinarOnDemand.substring(webinarOnDemand.indexOf('[') + 1, webinarOnDemand.length - 1) : '';
        const onDemandInWebinarZone = DateTime.fromISO(webinarOnDemand).setZone(webinarTimeZone);
        const nowInWebinarZone = DateTime.now().setZone(webinarTimeZone);
        const siteTimezone = document.querySelector('html').getAttribute('data-timezone');

        let locale = (document.documentElement.lang || 'en-US')
            .replace('-__', '')
            .replace('_', '-');
        if (locale.toLowerCase() === 'en-mde') {
            locale = 'en-GB';
        }

        let hasFutureDate = true;

        if (nowInWebinarZone > onDemandInWebinarZone) { // On-Demand date has passed
            hasFutureDate = false;
        }

        const webinarInstances = webinarTile.getAttribute('data-webinar-instances');
        if (webinarInstances != null) {
            const instanceArrayAll = webinarInstances.split(',');
            let instanceArrayFuture = [];
            for (let i = 0; i < instanceArrayAll.length; i += 1) {
                if (instanceArrayAll[i] > nowInWebinarZone.toString()) {
                    instanceArrayFuture = instanceArrayAll.slice(i);
                    break;
                }
            }

            if (instanceArrayFuture.length === 0) {
                hasFutureDate = false;
                // All instance dates have passed, so replace initial webinar details with last (most recent) instance
                const instanceToDisplay = instanceArrayAll[instanceArrayAll.length - 1].split('--');

                tileDate?.remove();
                tileTime?.remove();
                if (instanceToDisplay[1] !== 'null' && tileLength) {
                    tileLength.innerHTML = tileLength.innerHTML.substring(0, tileLength.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[1]);
                } else {
                    tileLength?.remove();
                }
                if (instanceToDisplay[2] !== 'null' && tileLang) {
                    tileLang.innerHTML = tileLang.innerHTML.substring(0, tileLang.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[2]);
                } else {
                    tileLang?.remove();
                }
            } else {
                hasFutureDate = true;
                // Replace initial webinar details with next future instance
                const instanceToDisplay = instanceArrayFuture[0].split('--');
                const webinarDateTime = instanceToDisplay[0];
                const displayDateTime = DateTime.fromISO(webinarDateTime).setZone(siteTimezone);
                const displayDate = displayDateTime.setLocale(locale).toLocaleString(DateTime.DATE_FULL);
                const displayTime = displayDateTime.setLocale(locale).toLocaleString(DateTime.TIME_SIMPLE).replace(/M/, '.M.')
                    .concat(' ', siteTimezone === 'Europe/Istanbul' ? 'Turkey Time' : displayDateTime.setLocale(locale).toFormat('ZZZZZ'));

                if (tileDate) {
                    tileDate.innerHTML = tileDate.innerHTML.substring(0, tileDate.innerHTML.indexOf('/span>') + 7).concat(displayDate);
                }
                if (tileTime) {
                    tileTime.innerHTML = tileTime.innerHTML.substring(0, tileTime.innerHTML.indexOf('/span>') + 7).concat(displayTime);
                }
                tileLength?.remove();
                if (instanceToDisplay[2] !== 'null' && tileLang) {
                    tileLang.innerHTML = tileLang.innerHTML.substring(0, tileLang.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[2]);
                } else {
                    tileLang?.remove();
                }
            }
        }

        if (hasFutureDate) {
            webinarTile.setAttribute('data-webinar-type', `future ${webinarType}`);
            tileLength?.remove();
        } else {
            webinarTile.setAttribute('data-webinar-type', `past ${webinarType}`);
            tileDate?.remove();
            tileTime?.remove();
        }
    };

    /**
     * Updates static webinar tiles with dynamic date/time information and webinar labels
     */
    updateStaticWebinarTiles = () => {
        for (let i = 0; i < this.elements.staticContentWebinarContainerElements.length; i += 1) {
            const webinarTile = this.elements.staticContentWebinarContainerElements[i];
            this.updateSingleStaticWebinarTile(webinarTile);
        }
    };

    /**
     * Updates a single static webinar tile with dynamic date/time information
     * @param webinarTile - the static webinar tile container element
     */
    updateSingleStaticWebinarTile = (webinarTile) => {
        const tileDate = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.startDate`);
        const tileTime = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.startTime`);
        const tileLength = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.length`);
        const tileLang = webinarTile.querySelector(`.${contentTileClassName}__webinar-detail.language`);
        const webinarType = webinarTile.getAttribute('data-webinar-type');
        const webinarOnDemand = webinarTile.getAttribute('data-webinar-on-demand');

        if (!webinarType || !webinarOnDemand) {
            return; // Skip if essential data is missing
        }

        const webinarTimeZone = webinarOnDemand.substring(webinarOnDemand.indexOf('[') + 1, webinarOnDemand.length - 1);
        const onDemandInWebinarZone = DateTime.fromISO(webinarOnDemand).setZone(webinarTimeZone);
        const nowInWebinarZone = DateTime.now().setZone(webinarTimeZone);
        const siteTimezone = document.querySelector('html').getAttribute('data-timezone');

        let locale = (document.documentElement.lang || 'en-US')
            .replace('-__', '')
            .replace('_', '-');
        if (locale.toLowerCase() === 'en-mde') {
            locale = 'en-GB';
        }

        let hasFutureDate = true;

        if (nowInWebinarZone > onDemandInWebinarZone) { // On-Demand date has passed
            hasFutureDate = false;
        }

        const webinarInstances = webinarTile.getAttribute('data-webinar-instances');
        if (webinarInstances != null) {
            const instanceArrayAll = webinarInstances.split(',');
            let instanceArrayFuture = [];
            for (let i = 0; i < instanceArrayAll.length; i += 1) {
                if (instanceArrayAll[i] > nowInWebinarZone.toString()) {
                    instanceArrayFuture = instanceArrayAll.slice(i);
                    break;
                }
            }

            if (instanceArrayFuture.length === 0) {
                hasFutureDate = false;
                // All instance dates have passed, so replace initial webinar details with last (most recent) instance
                const instanceToDisplay = instanceArrayAll[instanceArrayAll.length - 1].split('--');

                tileDate?.remove();
                tileTime?.remove();
                if (instanceToDisplay[1] !== 'null' && tileLength) {
                    tileLength.innerHTML = tileLength.innerHTML.substring(0, tileLength.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[1]);
                } else {
                    tileLength?.remove();
                }
                if (instanceToDisplay[2] !== 'null' && tileLang) {
                    tileLang.innerHTML = tileLang.innerHTML.substring(0, tileLang.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[2]);
                } else {
                    tileLang?.remove();
                }
            } else {
                hasFutureDate = true;
                // Replace initial webinar details with next future instance
                const instanceToDisplay = instanceArrayFuture[0].split('--');
                const webinarDateTime = instanceToDisplay[0];
                const displayDateTime = DateTime.fromISO(webinarDateTime).setZone(siteTimezone);
                const displayDate = displayDateTime.setLocale(locale).toLocaleString(DateTime.DATE_FULL);
                const displayTime = displayDateTime.setLocale(locale).toLocaleString(DateTime.TIME_SIMPLE).replace(/M/, '.M.')
                    .concat(' ', siteTimezone === 'Europe/Istanbul' ? 'Turkey Time' : displayDateTime.setLocale(locale).toFormat('ZZZZZ'));

                if (tileDate) {
                    tileDate.innerHTML = tileDate.innerHTML.substring(0, tileDate.innerHTML.indexOf('/span>') + 7).concat(displayDate);
                }
                if (tileTime) {
                    tileTime.innerHTML = tileTime.innerHTML.substring(0, tileTime.innerHTML.indexOf('/span>') + 7).concat(displayTime);
                }
                tileLength?.remove();
                if (instanceToDisplay[2] !== 'null' && tileLang) {
                    tileLang.innerHTML = tileLang.innerHTML.substring(0, tileLang.innerHTML.indexOf('/span>') + 7).concat(instanceToDisplay[2]);
                } else {
                    tileLang?.remove();
                }
            }
        }

        // Update webinar type attribute and handle labels
        if (hasFutureDate) {
            webinarTile.setAttribute('data-webinar-type', `future ${webinarType}`);
            tileLength?.remove();
            // Show future webinar label
            const futureLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_future`);
            const pastLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_past`);
            const seriesLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_series`);

            if (webinarType.toLowerCase() === 'series') {
                seriesLabel?.classList.add('active');
                futureLabel?.classList.remove('active');
                pastLabel?.classList.remove('active');
            } else {
                futureLabel?.classList.add('active');
                pastLabel?.classList.remove('active');
                seriesLabel?.classList.remove('active');
            }
        } else {
            webinarTile.setAttribute('data-webinar-type', `past ${webinarType}`);
            tileDate?.remove();
            tileTime?.remove();
            // Show past webinar label
            const futureLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_future`);
            const pastLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_past`);
            const seriesLabel = webinarTile.querySelector(`.${contentTileClassName}__webinar-label_series`);

            pastLabel?.classList.add('active');
            futureLabel?.classList.remove('active');
            seriesLabel?.classList.remove('active');
        }
    };
}

registerComponent(`.${componentName}`, RelatedContentGroup);
registerComponent(`.xf-${componentName}`, RelatedContentGroup);
